import  { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { experienceService } from '../../services/dataService';
import toast, { Toaster } from 'react-hot-toast';
import { ArrowLeft, Save } from 'lucide-react';

const ExperienceEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    role: '',
    company: '',
    period: '',
    description: '',
    image: '',
    order: 0
  });

  useEffect(() => {
    if (isEditing) {
      fetchExperience();
    }
  }, [id]);

  const fetchExperience = async () => {
    try {
      const result = await experienceService.getById(id);
      if (result.success) {
        setFormData(result.data);
      } else {
        toast.error('Failed to load experience entry');
      }
    } catch (error) {
      toast.error('Error loading experience entry');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.role || !formData.company || !formData.period) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      let result;
      if (isEditing) {
        result = await experienceService.update(id, formData);
      } else {
        result = await experienceService.create(formData);
      }

      if (result.success) {
        toast.success(`Experience ${isEditing ? 'updated' : 'created'} successfully!`);
        setTimeout(() => {
          navigate('/adminaccess/experience');
        }, 500);
      } else {
        toast.error(`Failed to ${isEditing ? 'update' : 'create'} experience entry`);
      }
    } catch (error) {
      toast.error('Error saving experience entry');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white font-mono flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin mb-4"></div>
          <p className="text-white/60">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white font-mono">
      <Toaster 
        position="top-center"
        toastOptions={{
          style: {
            background: '#1a1a1a',
            color: '#fff',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/adminaccess/experience')}
            className="flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 border border-green-500/30 rounded-lg transition-colors disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            <span>{saving ? 'Saving...' : 'Save Experience'}</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-light mb-2">
            {isEditing ? 'Edit Experience' : 'Add Experience'}
          </h1>
          <p className="text-white/60">
            {isEditing ? '' : 'Add a new work experience to your profile'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Role */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Role/Position <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="role"
              value={formData.role}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., Software Developer Intern"
            />
          </div>

          {/* Company */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Company <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="company"
              value={formData.company}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., DirectMail.io"
            />
          </div>

          {/* Period */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Period <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              name="period"
              value={formData.period}
              onChange={handleChange}
              required
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., Jan 2025 – Jul 2025"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={3}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors resize-none"
              placeholder="Brief description of your role and responsibilities"
            />
          </div>

          {/* Image URL */}
          <div>
            <label className="block text-white/60 text-sm mb-2">
              Image URL
            </label>
            <input
              type="url"
              name="image"
              value={formData.image}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="e.g., images/directmail.jpg or https://example.com/logo.jpg"
            />
            <p className="text-white/40 text-xs mt-1">
              Provide a relative path or full URL to the company logo
            </p>
          </div>

          {/* Order */}
          <div>
            <label className="block text-white/60 text-sm mb-2">Display Order</label>
            <input
              type="number"
              name="order"
              value={formData.order}
              onChange={handleChange}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-white/30 transition-colors"
              placeholder="0"
            />
            <p className="text-white/40 text-xs mt-1">
              Lower numbers appear first (0 = first position)
            </p>
          </div>

          {/* Preview */}
          <div className="p-6 rounded-lg border border-white/10 bg-white/5">
            <h3 className="text-sm text-white/40 uppercase tracking-wider mb-4">Preview</h3>
            <div className="flex gap-6">
              {formData.image && (
                <img
                  src={formData.image}
                  alt={formData.company}
                  className="w-20 h-20 rounded-xl border border-white/10 object-cover flex-shrink-0"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-1">
                  <h3 className="text-lg font-medium">{formData.role || 'Role'}</h3>
                  <div className="text-white/60">{formData.company || 'Company'}</div>
                  <div className="text-white/40 text-sm mt-1">{formData.period || 'Period'}</div>
                  {formData.description && (
                    <p className="text-white/50 text-sm leading-relaxed mt-1">
                      {formData.description}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </form>
      </main>
    </div>
  );
};

export default ExperienceEditor;